import React, { useEffect, useMemo, useState } from 'react';
import { Box, Container, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Outlet, useLocation } from 'react-router';
import Header from '../components/WebHeader';
import Footer from '../components/Footer';
import CookieConsent from '../components/CookieConsent';
import MobileAppBanner from '../components/MobileAppBanner';
// import FloatingBetaBadge from '../components/FloatingBetaBadge';
import ChatManager from '../components/chat/ChatManager';
import PodChatManager from '../components/Pods/PodChatManager';
import SystemBanner from '../components/SystemBanner';
import { BaselineSchemaProvider } from '../components/SEO/BaselineSchemaProvider';
import { useSession } from '../SessionContext';
import { IUserSession } from '../interfaces/IUserSession';
import { useApiClient } from '../ApiClientProvider';
import { NetworkScoreService } from '../services/networkScoreService';
import { LevelUpDialog } from '../components/score';
import {
    runAfterPageIsInteractive,
    shouldDeferNonCriticalScriptsForRoute,
} from '../utils/thirdPartyScripts';

const Public: React.FC = () => {
    const location = useLocation();
    const { session } = useSession<IUserSession>();
    const applicationClient = useApiClient('applicationClient');
    const networkScoreService = useMemo(
        () => new NetworkScoreService(applicationClient),
        [applicationClient],
    );
    const [showChatManager, setShowChatManager] = useState(
        () => !shouldDeferNonCriticalScriptsForRoute(location.pathname),
    );

    const environment = useMemo(() => {
        const hostname = window.location.hostname;
        if (hostname === 'localhost' || hostname.endsWith('.localhost')) return 'Development';
        const parts = hostname.split('.');
        if (parts.length > 2) {
            if (parts[0].toLowerCase() === 'dev') return 'Development';
            if (parts[0].toLowerCase() === 'qa') return 'Testing';
        }
        return null;
    }, []);

    useEffect(() => {
        if (shouldDeferNonCriticalScriptsForRoute(location.pathname)) {
            setShowChatManager(false);
            return runAfterPageIsInteractive(() => {
                setShowChatManager(true);
            });
        }

        setShowChatManager(true);
        return;
    }, [location.pathname]);

    return (
        <BaselineSchemaProvider>
            <Box
                sx={{
                    minHeight: '100vh',
                    bgcolor: 'background.default',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <Header />
                <SystemBanner />
                <MobileAppBanner />
                <Container
                    maxWidth={false}
                    component="main"

                    sx={{
                        px: { xs: 0 },
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <Outlet />
                </Container>
                <Footer />
                <CookieConsent />
                {showChatManager && <ChatManager />}
                {showChatManager && <PodChatManager />}
                {!!session?.token && <LevelUpDialog networkScoreService={networkScoreService} />}
                {/* Non-production environment indicator */}
                {environment && (
                    <Box
                        sx={{
                            position: 'fixed',
                            bottom: 12,
                            left: 12,
                            zIndex: 9999,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 1.25,
                            py: 0.5,
                            borderRadius: '20px',
                            backgroundColor: alpha(environment === 'Development' ? '#f59e0b' : '#3b82f6', 0.12),
                            border: `1px solid ${alpha(environment === 'Development' ? '#f59e0b' : '#3b82f6', 0.35)}`,
                            backdropFilter: 'blur(8px)',
                            pointerEvents: 'none',
                        }}
                    >
                        <Box
                            sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                bgcolor: environment === 'Development' ? '#f59e0b' : '#3b82f6',
                                boxShadow: `0 0 5px ${environment === 'Development' ? '#f59e0b' : '#3b82f6'}`,
                                flexShrink: 0,
                            }}
                        />
                        <Typography
                            sx={{
                                fontSize: '0.68rem',
                                fontWeight: 600,
                                color: environment === 'Development' ? '#b45309' : '#1d4ed8',
                                letterSpacing: 0.3,
                                lineHeight: 1,
                                userSelect: 'none',
                            }}
                        >
                            {environment}
                        </Typography>
                    </Box>
                )}
            </Box>
        </BaselineSchemaProvider>
    )
};

export default Public;